import React from "react";

export type SummaryTheme = "light" | "dark";

/** Single spacing scale — used by every summary section and child */
export const SUMMARY_SPACING = {
  pageX: "px-4 sm:px-6",
  pageY: "pt-4 pb-6 sm:pt-5 sm:pb-8",
  stack: "gap-4",
  grid: "gap-4",
  sectionHeader: "px-4 py-2.5",
  sectionBody: "px-4 py-3",
  sectionIcon: "h-7 w-7",
  innerStack: "space-y-2.5",
  innerGrid: "gap-2.5",
  rowY: "py-2 sm:py-2.5",
  cardPad: "p-3",
  tableCell: "py-2 px-3",
  tableHead: "py-2 px-3",
  emptyMinH: "min-h-[2.5rem] flex items-center",
  loading: "py-5",
  scrollMt: "scroll-mt-[4.25rem]",
} as const;

export function summarySectionId(key: string) {
  return `patient-summary-${key}`;
}

export function SummarySection({
  id,
  title,
  icon,
  titleExtra,
  badge,
  children,
  theme = "light",
  className = "",
}: {
  id: string;
  title: string;
  icon?: React.ReactNode;
  /** Rendered next to the title (e.g. sort controls). */
  titleExtra?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  theme?: SummaryTheme;
  className?: string;
}) {
  const isDark = theme === "dark";

  return (
    <section
      id={id}
      className={`flex h-full flex-col rounded-2xl border shadow-sm ${
        isDark
          ? "border-slate-700/80 bg-slate-800/60 shadow-black/20"
          : "border-slate-200/90 bg-white shadow-slate-200/60"
      } ${className}`}
    >
      <div
        className={`flex items-center justify-between gap-3 border-b ${SUMMARY_SPACING.sectionHeader} ${
          isDark ? "border-slate-700/80 bg-slate-800/80" : "border-slate-100 bg-slate-50/80"
        }`}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {icon && (
            <span
              className={`flex ${SUMMARY_SPACING.sectionIcon} shrink-0 items-center justify-center rounded-lg ${
                isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-50 text-indigo-600"
              }`}
            >
              {icon}
            </span>
          )}
          <h3
            className={`truncate text-sm font-semibold tracking-tight ${
              isDark ? "text-slate-100" : "text-slate-900"
            }`}
          >
            {title}
          </h3>
          {titleExtra}
        </div>
        {badge}
      </div>
      <div className={`flex-1 ${SUMMARY_SPACING.sectionBody}`}>{children}</div>
    </section>
  );
}

export function SummaryBadge({
  children,
  tone = "neutral",
  theme = "light",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "info" | "success" | "warning" | "danger";
  theme?: SummaryTheme;
}) {
  const isDark = theme === "dark";
  const tones = {
    neutral: isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600",
    info: isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-50 text-indigo-700",
    success: isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700",
    warning: isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-50 text-amber-700",
    danger: isDark ? "bg-red-500/20 text-red-300" : "bg-red-50 text-red-700",
  };

  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function SummaryRows({
  rows,
  theme = "light",
}: {
  rows: Array<{ label: string; value: React.ReactNode; multiline?: boolean }>;
  theme?: SummaryTheme;
}) {
  if (rows.length === 0) return null;

  const isDark = theme === "dark";
  const labelClass = `shrink-0 text-[10px] font-bold uppercase tracking-wider ${
    isDark ? "text-slate-500" : "text-slate-400"
  }`;
  const valueClass = `text-sm font-medium leading-snug ${
    isDark ? "text-slate-100" : "text-slate-800"
  }`;

  return (
    <ul className={`divide-y ${isDark ? "divide-slate-700/60" : "divide-slate-100"}`}>
      {rows.map((row) => (
        <li
          key={row.label}
          className={
            row.multiline
              ? `first:pt-0 last:pb-0 ${SUMMARY_SPACING.rowY}`
              : `flex items-baseline gap-2.5 first:pt-0 last:pb-0 sm:gap-3 ${SUMMARY_SPACING.rowY}`
          }
        >
          {row.multiline ? (
            <>
              <p className={`${labelClass} mb-0.5`}>{row.label}</p>
              <p className={`${valueClass} break-words`}>{row.value}</p>
            </>
          ) : (
            <>
              <span className={`${labelClass} w-[6.5rem] sm:w-[7rem]`}>{row.label}</span>
              <span className={`${valueClass} min-w-0 flex-1 break-words`}>{row.value}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

export function SummaryEmpty({
  message,
  theme = "light",
}: {
  message: string;
  theme?: SummaryTheme;
}) {
  return (
    <p
      className={`text-sm italic ${SUMMARY_SPACING.emptyMinH} ${
        theme === "dark" ? "text-slate-500" : "text-slate-400"
      }`}
    >
      {message}
    </p>
  );
}

export function SummaryTableWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mx-4 overflow-x-auto">
      <div className="inline-block min-w-full px-4 align-middle">{children}</div>
    </div>
  );
}

export function summaryTableHead(theme: SummaryTheme) {
  return `text-left ${SUMMARY_SPACING.tableHead} text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${
    theme === "dark" ? "bg-slate-900/50 text-slate-400" : "bg-slate-50 text-slate-500"
  }`;
}

export function summaryTableCell(theme: SummaryTheme) {
  return `${SUMMARY_SPACING.tableCell} align-middle text-xs ${
    theme === "dark" ? "text-slate-300" : "text-slate-600"
  }`;
}

export function SummaryLoading({ theme = "light" }: { theme?: SummaryTheme }) {
  return (
    <div className={`flex items-center justify-center ${SUMMARY_SPACING.loading}`}>
      <div
        className={`h-7 w-7 animate-spin rounded-full border-2 border-t-transparent ${
          theme === "dark" ? "border-indigo-400" : "border-indigo-500"
        }`}
      />
    </div>
  );
}

/** Inner card used inside medical / alert blocks */
export function summaryInnerCard(theme: SummaryTheme, alert = false) {
  const isDark = theme === "dark";
  if (alert) {
    return `rounded-xl ${SUMMARY_SPACING.cardPad} ring-1 ${
      isDark ? "bg-red-500/10 ring-red-500/25" : "bg-red-50/80 ring-red-100"
    }`;
  }
  return `rounded-xl ${SUMMARY_SPACING.cardPad} ring-1 ${
    isDark ? "bg-slate-900/40 ring-slate-700/60" : "bg-slate-50 ring-slate-100"
  }`;
}

export function summaryLabelClass(theme: SummaryTheme) {
  return `text-[10px] font-semibold uppercase tracking-wider ${
    theme === "dark" ? "text-slate-500" : "text-slate-400"
  }`;
}
